import { useEffect, useState } from 'react'

import { Image } from 'antd-mobile'
import { useKeenSlider } from 'keen-slider/react'

import { isSuccess } from '@/axios'
import { getBanner, IBannerListItem, historyGetByResource } from '@/api'
import { CONTENT_ROUTE_PATH, EPublishType } from '@/types'
import { store, loginStateAtom, useMobileStatusAtom } from '@/store'
import SkeletonState from '@/components/CommonState/SkeletonState'

import { cn, customJumpHref } from '@/utils'
import Icon from '@/components/Icon'

interface PropsIn {
  className?: string
}

const regex = /\/detail\/novel(?:\/|\?|$)/

function Arrow(props: { onClick?: React.MouseEventHandler; disabled?: boolean; left?: boolean; className?: string }) {
  return (
    <div
      className={cn(
        `flex -translate-y-1/2 cursor-pointer items-center justify-center p-[20px] ${props.left ? 'left-[35px]' : 'left-auto right-[35px]'} absolute top-1/2 rounded-full bg-color-black-trans30`,
        props.className,
      )}
      onClick={(e) => {
        if (props?.disabled) return
        props?.onClick?.(e)
      }}
    >
      <Icon name={props.left ? 'Back' : 'Next'} className={cn(`h-[40px] w-[40px] transition-all duration-300 hover:scale-110`)} />
    </div>
  )
}

const DiscoverSwiper = (props: PropsIn) => {
  const [load, setLoad] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slidesCount, setSlidesCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel)
    },
    created() {
      setLoad(true)
    },
    loop: true,
  })
  const { className } = props
  const [data, setData] = useState<IBannerListItem[]>([])
  const isLogin = store.get(loginStateAtom)

  const getData = async () => {
    setIsLoading(true)
    const resp = await getBanner()
    setIsLoading(false)
    if (isSuccess(resp)) {
      return setData(resp.data.list)
    }
  }

  const handleClickJump = async (item: IBannerListItem) => {
    if (!Object.keys(item.pic_jump_url).length) {
      return
    }
    const jumpUrl = item.pic_jump_url[2]
    const novelRegex = regex.test(jumpUrl)

    if (isLogin && novelRegex) {
      const resourceType = jumpUrl.split('/').at(-1)!.split('-')[0]
      const resourceId = jumpUrl.split('/').at(-1)!.split('-')[1]
      const resp = await historyGetByResource({
        resource_id: resourceId,
        resource_type: Number(resourceType),
      })
      if (isSuccess(resp) && resp.data.is_history) {
        return customJumpHref(
          `/detail/${CONTENT_ROUTE_PATH.get(
            Number(resourceType) as unknown as EPublishType,
          )}/${resourceType}-${resourceId}-${resp.data.history_data.chapter_id}`,
        )
      }
    }
    customJumpHref(item.pic_jump_url[2])
  }

  const { mobileStatus } = useMobileStatusAtom()

  useEffect(() => {
    getData()
  }, [])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        getData()
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  useEffect(() => {
    if (instanceRef.current && data) {
      instanceRef.current.update()
      setSlidesCount(instanceRef.current.track.details.slides.length)
    }
  }, [data])

  if (isLoading) {
    return (
      <div className={cn('relative', className ? className : '')}>
        <SkeletonState type={['swiper']} />
      </div>
    )
  }

  return data?.length ? (
    <div className={cn(className ? className : '')}>
      <div className='relative'>
        <div className='relative'>
          <div ref={sliderRef} className='keen-slider max-h-[257px] w-full !max-w-full rounded-[15px] md:max-h-[690px]'>
            {data?.map((item) => {
              return (
                <div
                  className='keen-slider__slide cursor-pointer overflow-hidden rounded-[15px] text-base-text-color'
                  key={item.id}
                  onClick={() => {
                    handleClickJump(item)
                  }}
                >
                  <Image src={item.pic_url} width='100%' height='100%' fit='cover' lazy placeholder={null} />
                </div>
              )
            })}
          </div>
          {!mobileStatus && load && (
            <>
              <Arrow
                left
                onClick={(e) => {
                  e.stopPropagation()
                  instanceRef.current?.prev()
                }}
              />

              <Arrow
                onClick={(e) => {
                  e.stopPropagation()
                  instanceRef.current?.next()
                }}
              />
            </>
          )}
        </div>
        <div className='absolute bottom-[-22px] left-1/2 flex -translate-x-1/2'>
          {load &&
            instanceRef.current &&
            [...Array(slidesCount).keys()].map((idx) => {
              return (
                <div
                  className={cn(
                    currentSlide === idx
                      ? 'mx-[11px] h-[11px] w-[22px] rounded-[6px] bg-base-dolist-active-color'
                      : 'mx-[11px] h-[11px] w-[11px] rounded-[6px] bg-base-dolist-bg-color',
                    'md:cursor-pointer',
                  )}
                  key={idx}
                  onClick={() => {
                    instanceRef.current?.moveToIdx(idx)
                  }}
                ></div>
              )
            })}
        </div>
      </div>
    </div>
  ) : null
}

export default DiscoverSwiper
